import React, { useEffect, useState, useCallback } from "react";
import axios from "../../axiosInstace";
import { useNavigate } from "react-router-dom";
import "./CandidateRequirement.scss";

const API = `${process.env.REACT_APP_API_CONSULTANT_URL}/consultancy/candidate-requirement`;

// ─── Constants ─────────────────────────────────────────────────────────────────
const COLORS = [
  "#6366f1",
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
];

const STATUS_META = {
  ACTIVE: { color: "#15803d", bg: "#dcfce7", label: "Active" },
  INACTIVE: { color: "#b91c1c", bg: "#fee2e2", label: "Inactive" },
};

// ─── Helpers ────────────────────────────────────────────────────────────────────
const getInitials = (name = "") =>
  name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0] || "")
    .join("")
    .toUpperCase() || "?";

const avatarColor = (name = "") =>
  COLORS[((name || "").charCodeAt(0) || 0) % COLORS.length];

const fmtBudget = (budget, budgetType, salaryType) => {
  if (!budget) return "—";

  let text = `₹${Number(budget).toLocaleString("en-IN")}`;

  if (budgetType) text += ` / ${budgetType}`;
  if (salaryType) text += ` (${salaryType})`;

  return text;
};

// ─── CSV Export ─────────────────────────────────────────────────────────────────
const exportToCSV = (data) => {
  const headers = [
    "ID",
    "Company",
    "Job Title",
    "Role",
    "Experience",
    "Budget (₹)",
    "Vacancies",
    "Job Code",
    "Status",
    "Job Post Created",
    "Source",
  ];

  const escape = (val) => {
    const str = val === null || val === undefined ? "" : String(val);
    return str.includes(",") || str.includes('"') || str.includes("\n")
      ? `"${str.replace(/"/g, '""')}"`
      : str;
  };

  const rows = data.map((r) =>
    [
      r.candidateRequirementId,
      r.recruitmentCompanyName || "",
      r.jobTitle || "",
      r.role || "",
      r.experience || "",
      r.budget || "",
      r.noOfVacancy || "",
      r.jobCode || "",
      r.status || "",
      r.consultancyJobPostId !== null ? "Yes" : "No",
      r.moduleHistoryName || "",
    ]
      .map(escape)
      .join(","),
  );

  const csv = [headers.join(","), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `candidate-requirements-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// ─── Stat Card ──────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, accent }) => (
  <div className="hfd-stat" style={{ "--accent": accent }}>
    <div className="hfd-stat__body">
      <div className="hfd-stat__text">
        <p className="hfd-stat__label">{label}</p>
        {sub && <p className="hfd-stat__sub">{sub}</p>}
      </div>

      <p className="hfd-stat__value">{value}</p>
    </div>

    <div className="hfd-stat__bar" />
  </div>
);

// ─── Status Badge ───────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || {
    color: "#6b7280",
    bg: "#f3f4f6",
    label: status,
  };
  return (
    <span className="cr-badge" style={{ color: m.color, background: m.bg }}>
      {m.label}
    </span>
  );
};

// ─── Donut Chart ────────────────────────────────────────────────────────────────
const DonutChart = ({ segments, size = 72 }) => {
  const r = (size - 12) / 2;
  const cx = size / 2;
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  let cumulative = 0;
  const slices = segments
    .filter((s) => s.value > 0)
    .map((seg) => {
      const pct = seg.value / total;
      const start = cumulative;
      cumulative += pct;
      const startAngle = start * 2 * Math.PI - Math.PI / 2;
      const endAngle = cumulative * 2 * Math.PI - Math.PI / 2;
      const x1 = cx + r * Math.cos(startAngle);
      const y1 = cx + r * Math.sin(startAngle);
      const x2 = cx + r * Math.cos(endAngle);
      const y2 = cx + r * Math.sin(endAngle);
      const large = pct > 0.5 ? 1 : 0;
      return {
        ...seg,
        d: `M ${cx} ${cx} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`,
      };
    });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {slices.map((s, i) => (
        <path key={i} d={s.d} fill={s.color} opacity="0.9" />
      ))}
      <circle cx={cx} cy={cx} r={r * 0.58} fill="white" />
      <text
        x={cx}
        y={cx + 4}
        textAnchor="middle"
        fontSize="11"
        fontWeight="600"
        fill="#1e293b"
      >
        {total}
      </text>
    </svg>
  );
};

// ─── Spark Bar Row ──────────────────────────────────────────────────────────────
const SparkRow = ({ label, value, max, amber = false }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="cr-spark-row">
      <span className="cr-spark-row__label" title={label}>
        {label.length > 18 ? label.slice(0, 17) + "…" : label}
      </span>
      <div className="cr-spark-row__track">
        <div
          className={`cr-spark-row__fill${amber ? " cr-spark-row__fill--amber" : ""}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="cr-spark-row__val">{value}</span>
    </div>
  );
};

// ─── Expanded Card Body ─────────────────────────────────────────────────────────
const CardBody = ({ r, onCreatePost, onViewCandidates }) => {
  const hasPost = r.consultancyJobPostId !== null;
  const isActive = r.status?.toUpperCase() === "ACTIVE";

  return (
    <div className="cr-card__body">
      <div className="cr-card__section">
        <p className="cr-card__section-label">Requirement details</p>
        <div className="cr-detail-grid">
          <div className="cr-detail-item">
            <p className="cr-detail-item__label">Job title</p>
            <p className="cr-detail-item__val">{r.jobTitle || "—"}</p>
          </div>
          <div className="cr-detail-item">
            <p className="cr-detail-item__label">Role</p>
            <p className="cr-detail-item__val">{r.role || "—"}</p>
          </div>
          <div className="cr-detail-item">
            <p className="cr-detail-item__label">Experience</p>
            <p className="cr-detail-item__val">{r.experience || "—"}</p>
          </div>
          <div className="cr-detail-item">
            <p className="cr-detail-item__label">Budget</p>
            <p className="cr-detail-item__val">
              {fmtBudget(r.budget, r.budgetType, r.salaryType)}
            </p>
          </div>
          <div className="cr-detail-item">
            <p className="cr-detail-item__label">Vacancies</p>
            <p className="cr-detail-item__val">{r.noOfVacancy || "—"}</p>
          </div>
          <div className="cr-detail-item">
            <p className="cr-detail-item__label">Source</p>
            <p className="cr-detail-item__val">{r.moduleHistoryName || "—"}</p>
          </div>
        </div>
      </div>

      <div className="cr-action-row">
        {!hasPost && isActive ? (
          <button
            className="cr-action-btn cr-action-btn--primary"
            onClick={() => onCreatePost(r)}
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              <rect
                x="1.5"
                y="1.5"
                width="10"
                height="10"
                rx="1.5"
                stroke="currentColor"
                strokeWidth="1.4"
              />
              <path
                d="M6.5 4v5M4 6.5h5"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
            </svg>
            Create job post
          </button>
        ) : (
          <button className="cr-action-btn cr-action-btn--ghost" disabled>
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              <path
                d="M2.5 6.5l3 3 5-5"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Job post created
          </button>
        )}
        <button
          className="cr-action-btn cr-action-btn--secondary"
          onClick={() => onViewCandidates(r)}
        >
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
            <circle
              cx="6.5"
              cy="4"
              r="2.5"
              stroke="currentColor"
              strokeWidth="1.4"
            />
            <path
              d="M1.5 12v-1.5a5 5 0 0110 0V12"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
          View candidates
        </button>
      </div>
    </div>
  );
};

// ─── Main Page ──────────────────────────────────────────────────────────────────
const CandidateRequirement = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [expandedId, setExpandedId] = useState(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    axios
      .get(API)
      .then((res) => setData(res.data.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Computed stats ──
  const totalActive = data.filter(
    (r) => r.status?.toUpperCase() === "ACTIVE",
  ).length;
  const totalPosts = data.filter((r) => r.consultancyJobPostId !== null).length;
  const totalVacancies = data.reduce((s, r) => s + (r.noOfVacancy || 0), 0);

  // ── Sidebar analytics ──
  const byCompanyVacancy = data.reduce((acc, r) => {
    const c = r.recruitmentCompanyName || "Unknown";
    acc[c] = (acc[c] || 0) + (r.noOfVacancy || 0);
    return acc;
  }, {});
  const maxVacancy = Math.max(...Object.values(byCompanyVacancy), 1);

  const byCompanyBudget = data.reduce((acc, r) => {
    const c = r.recruitmentCompanyName || "Unknown";
    acc[c] = (acc[c] || 0) + (r.budget || 0);
    return acc;
  }, {});
  const maxBudget = Math.max(...Object.values(byCompanyBudget), 1);

  // ── Filter ──
  const filtered = data.filter((r) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      (r.role || "").toLowerCase().includes(q) ||
      (r.jobCode || "").toLowerCase().includes(q) ||
      (r.jobTitle || "").toLowerCase().includes(q) ||
      (r.recruitmentCompanyName || "").toLowerCase().includes(q);
    const matchStatus =
      statusFilter === "ALL" || r.status?.toUpperCase() === statusFilter;
    return matchSearch && matchStatus;
  });

  const toggleExpand = (id) =>
    setExpandedId((prev) => (prev === id ? null : id));

  const handleCreatePost = (r) =>
    navigate("/consultant/job-posts/all", {
      state: {
        openCreateDrawer: true,
        candidateRequirementId: r.candidateRequirementId,
      },
    });

  const handleViewCandidates = () => navigate("/consultant/candidates/all");

  return (
    <div className="cr-page">
      {/* ── Header ── */}
      <div className="cr-page__header">
        <div className="cr-page__header-left">
          <div className="cr-page__title-wrap">
            <h1 className="cr-page__title">Company Requirements</h1>
            <span className="cr-page__count">{data.length} received</span>
          </div>
          <p className="cr-page__sub">
            Manage and track all incoming company requirements from client
            companies
          </p>
        </div>
        <button
          className="cr-btn cr-btn--primary cr-btn--lg"
          onClick={() => exportToCSV(data)}
          disabled={data.length === 0}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M7 1v8M4 6l3 3 3-3"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M2 11h10"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
          Export CSV
        </button>
      </div>

      {/* ── Stats ── */}
      <div className="hfd-stats-row">
        <StatCard
          label="Total requirements"
          value={data.length}
          sub={`${totalActive} active`}
          accent="#2563eb"
        />
        <StatCard
          label="Active requirements"
          value={totalActive}
          sub={`${data.length - totalActive} inactive`}
          accent="#15803d"
        />
        <StatCard
          label="Job posts created"
          value={totalPosts}
          sub={`${data.length - totalPosts} pending`}
          accent="#854f0b"
        />
        <StatCard
          label="Total vacancies"
          value={totalVacancies}
          sub="Across all requirements"
          accent="#6d28d9"
        />
      </div>

      {/* ── Toolbar ── */}
      <div className="cr-toolbar">
        <div className="cr-search-wrap">
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            className="cr-search-icon"
          >
            <circle cx="6" cy="6" r="4.5" stroke="#9ca3af" strokeWidth="1.3" />
            <path
              d="M9.5 9.5l2.5 2.5"
              stroke="#9ca3af"
              strokeWidth="1.3"
              strokeLinecap="round"
            />
          </svg>
          <input
            className="cr-search"
            placeholder="Search by role, title, job code, company…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="cr-search-clear" onClick={() => setSearch("")}>
              ×
            </button>
          )}
        </div>
        <div className="cr-filter-tabs">
          {[
            { key: "ALL", label: "All" },
            { key: "ACTIVE", label: "Active" },
            { key: "INACTIVE", label: "Inactive" },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`cr-filter-tab ${statusFilter === key ? "active" : ""}`}
              onClick={() => setStatusFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Content ── */}
      {loading ? (
        <div className="cr-loading">
          <div className="cr-loading__spinner" />
          <p>Loading candidate requirements…</p>
        </div>
      ) : data.length === 0 ? (
        <div className="cr-empty">
          <div className="cr-empty__icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="20" stroke="#e5e7eb" strokeWidth="2" />
              <path
                d="M16 24h16M24 16v16"
                stroke="#d1d5db"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <h3>No candidate requirements yet</h3>
          <p>Requirements received from client companies will appear here.</p>
        </div>
      ) : (
        <div className="cr-flow-grid">
          {/* ── Card list ── */}
          <div className="cr-flow-list">
            {filtered.length === 0 ? (
              <div className="cr-empty cr-empty--search">
                <p>
                  No requirements match your search.{" "}
                  <button
                    className="cr-link"
                    onClick={() => {
                      setSearch("");
                      setStatusFilter("ALL");
                    }}
                  >
                    Clear filters
                  </button>
                </p>
              </div>
            ) : (
              filtered.map((r) => {
                const isExp = expandedId === r.candidateRequirementId;
                const hasPost = r.consultancyJobPostId !== null;
                const isActive = r.status?.toUpperCase() === "ACTIVE";

                return (
                  <div
                    key={r.candidateRequirementId}
                    className={`cr-card ${isExp ? "cr-card--exp" : ""}`}
                  >
                    {/* Top row */}
                    <div
                      className="cr-card__top"
                      onClick={() => toggleExpand(r.candidateRequirementId)}
                    >
                      <div className="cr-card__left">
                        <div
                          className="cr-card__avatar"
                          style={{
                            background: avatarColor(r.recruitmentCompanyName),
                          }}
                        >
                          {getInitials(r.recruitmentCompanyName)}
                        </div>
                        <div className="cr-card__info">
                          <h3 className="cr-card__name">
                            {r.recruitmentCompanyName || "—"}
                          </h3>
                          <div className="cr-card__meta">
                            <span className="cr-meta-chip">
                              {r.jobTitle || "—"}
                            </span>
                            {r.jobCode && (
                              <span className="cr-meta-chip cr-meta-chip--code">
                                {r.jobCode}
                              </span>
                            )}
                            <StatusBadge status={r.status} />
                            {r.budget > 0 && (
                              <span className="cr-meta-chip">
                                {fmtBudget(
                                  r.budget,
                                  r.budgetType,
                                  r.salaryType,
                                )}
                              </span>
                            )}
                            {r.noOfVacancy > 0 && (
                              <span className="cr-meta-chip cr-meta-chip--vacancy">
                                <svg
                                  width="10"
                                  height="10"
                                  viewBox="0 0 10 10"
                                  fill="none"
                                >
                                  <circle
                                    cx="5"
                                    cy="3.5"
                                    r="2"
                                    stroke="currentColor"
                                    strokeWidth="1.1"
                                  />
                                  <path
                                    d="M1 9.5v-1a4 4 0 018 0v1"
                                    stroke="currentColor"
                                    strokeWidth="1.1"
                                    strokeLinecap="round"
                                  />
                                </svg>
                                {r.noOfVacancy}{" "}
                                {r.noOfVacancy === 1 ? "vacancy" : "vacancies"}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="cr-card__right">
                        <div
                          className="cr-card__actions"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {!hasPost && isActive && (
                            <button
                              className="cr-icon-btn cr-icon-btn--create"
                              onClick={() => handleCreatePost(r)}
                              title="Create job post"
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                              >
                                <rect
                                  x="1.5"
                                  y="1.5"
                                  width="11"
                                  height="11"
                                  rx="2"
                                  stroke="currentColor"
                                  strokeWidth="1.3"
                                />
                                <path
                                  d="M7 4.5v5M4.5 7h5"
                                  stroke="currentColor"
                                  strokeWidth="1.3"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </button>
                          )}
                          <button
                            className="cr-icon-btn cr-icon-btn--view"
                            onClick={() => handleViewCandidates(r)}
                            title="View candidates"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 14 14"
                              fill="none"
                            >
                              <circle
                                cx="7"
                                cy="5"
                                r="2.5"
                                stroke="currentColor"
                                strokeWidth="1.3"
                              />
                              <path
                                d="M2 13v-1.5a5 5 0 0110 0V13"
                                stroke="currentColor"
                                strokeWidth="1.3"
                                strokeLinecap="round"
                              />
                            </svg>
                          </button>
                        </div>
                        <svg
                          className={`cr-chevron ${isExp ? "cr-chevron--open" : ""}`}
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M4 6l4 4 4-4"
                            stroke="#9ca3af"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>

                    {/* Expanded body */}
                    {isExp && (
                      <CardBody
                        r={r}
                        onCreatePost={handleCreatePost}
                        onViewCandidates={handleViewCandidates}
                      />
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* ── Sidebar ── */}
          <aside className="cr-sidebar">
            {/* Status donut */}
            <div className="hfd-widget">
              <p className="hfd-widget__title">Status breakdown</p>
              <div className="hfd-donut-wrap">
                <DonutChart
                  size={84}
                  segments={[
                    { value: totalActive, color: "#15803d" },
                    { value: data.length - totalActive, color: "#ef4444" },
                  ]}
                />
                <div className="hfd-donut-legend">
                  {[
                    { label: "Active", val: totalActive, color: "#15803d" },
                    {
                      label: "Inactive",
                      val: data.length - totalActive,
                      color: "#ef4444",
                    },
                  ].map(({ label, val, color }) => (
                    <div key={label} className="hfd-donut-legend__row">
                      <span
                        className="hfd-donut-legend__dot"
                        style={{ background: color }}
                      />
                      <span className="hfd-donut-legend__label">{label}</span>
                      <span className="hfd-donut-legend__val">{val}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Vacancies per company */}
            <div className="hfd-widget">
              <p className="hfd-widget__title">Vacancies per company</p>
              <div className="hfd-sparkbars">
                {Object.entries(byCompanyVacancy).map(([company, val]) => (
                  <SparkRow
                    key={company}
                    label={company}
                    value={val}
                    max={maxVacancy}
                  />
                ))}
              </div>
            </div>

            {/* Budget per company */}
            <div className="hfd-widget">
              <p className="hfd-widget__title">Budget per company</p>
              <div className="hfd-sparkbars">
                {Object.entries(byCompanyBudget).map(([company, val]) => (
                  <SparkRow
                    key={company}
                    label={company}
                    value={`₹${(val / 100000).toFixed(1)}L`}
                    max={maxBudget}
                    amber
                  />
                ))}
              </div>
            </div>

            {/* Tips */}
            <div className="hfd-widget hfd-widget--tips">
              <p className="hfd-widget__title">Quick tips</p>
              <ul className="hfd-tips">
                <li>
                  <strong>Create job post</strong> is only available for active
                  requirements with no post yet.
                </li>
                <li>
                  Use <strong>Export CSV</strong> to share the full list with
                  stakeholders.
                </li>
                <li>Click any card to expand full requirement details.</li>
              </ul>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};

export default CandidateRequirement;
